/**
 * API Keys Page
 * API გასაღებების გვერდი
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDate } from '../utils/formatDate';
import {
  Card,
  Button,
  DataTable,
  Modal,
  Input,
  Spinner,
  useNotification
} from '@aiia/portal-core';
import type { TableColumn } from '@aiia/portal-core';
import { useApiKeys, useCreateApiKey, useRevokeApiKey, useDeleteApiKey } from '../hooks/useApiKeys';
import { getApiErrorMessage } from '../utils/errorMessages';
import type { CreateApiKeyRequest, ApiKey } from '../features/api/apiKeys';

interface ApiKeyFormData {
  name: string;
  allowedDomains: string;
  allowedIps: string;
}

const initialFormData: ApiKeyFormData = {
  name: '',
  allowedDomains: '',
  allowedIps: '',
};

export default function ApiKeysPage() {
  const { t } = useTranslation();
  const notification = useNotification();
  const { apiKeys, isLoading, refetch } = useApiKeys();
  const createApiKey = useCreateApiKey();
  const revokeApiKey = useRevokeApiKey();
  const deleteApiKey = useDeleteApiKey();

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isKeyModalOpen, setIsKeyModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [keyToDelete, setKeyToDelete] = useState<string | null>(null);
  const [newApiKey, setNewApiKey] = useState<string>('');
  const [formData, setFormData] = useState<ApiKeyFormData>(initialFormData);

  const handleCreate = async () => {
    if (!formData.name) {
      notification.error(t('apiKeys.enterName'));
      return;
    }

    try {
      const data: CreateApiKeyRequest = {
        name: formData.name,
        allowedDomains: formData.allowedDomains ? formData.allowedDomains.split(',').map(s => s.trim()) : undefined,
        allowedIps: formData.allowedIps ? formData.allowedIps.split(',').map(s => s.trim()) : undefined,
      };

      const response = await createApiKey.mutateAsync(data);

      setNewApiKey(response.rawKey);
      setIsCreateModalOpen(false);
      setIsKeyModalOpen(true);
      setFormData(initialFormData);
      refetch();
    } catch (error: unknown) {
      notification.error(getApiErrorMessage(error, t));
    }
  };

  const handleRevoke = async (keyId: string) => {
    try {
      await revokeApiKey.mutateAsync(keyId);
      notification.success(t('apiKeys.keyRevoked'));
      refetch();
    } catch (error: unknown) {
      notification.error(getApiErrorMessage(error, t));
    }
  };

  const confirmDelete = (keyId: string) => {
    setKeyToDelete(keyId);
    setDeleteModalOpen(true);
  };

  const handleDelete = async () => {
    if (!keyToDelete) return;
    try {
      await deleteApiKey.mutateAsync(keyToDelete);
      notification.success(t('apiKeys.keyDeleted'));
      setDeleteModalOpen(false);
      setKeyToDelete(null);
      refetch();
    } catch (error: unknown) {
      notification.error(getApiErrorMessage(error, t));
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(newApiKey);
    notification.success(t('apiKeys.keyCopied'));
  };

  const columns: TableColumn<ApiKey & Record<string, unknown>>[] = [
    {
      key: 'name',
      header: t('apiKeys.name'),
      render: (value) => <span className="font-medium">{String(value)}</span>,
    },
    {
      key: 'keyPrefix',
      header: t('apiKeys.keyPrefix'),
      render: (value) => (
        <code className="px-2 py-1 bg-gray-100 rounded text-sm">{String(value)}...</code>
      ),
    },
    {
      key: 'status',
      header: t('apiKeys.status'),
      render: (value) => (
        <span className={`px-2 py-1 text-xs rounded-full ${
          value === 'ACTIVE' ? 'bg-green-100 text-green-700' :
          value === 'REVOKED' ? 'bg-red-100 text-red-700' :
          'bg-yellow-100 text-yellow-700'
        }`}>
          {String(value)}
        </span>
      ),
    },
    {
      key: 'lastUsedAt',
      header: t('apiKeys.lastUsed'),
      render: (value) => value ? formatDate(String(value)) : t('apiKeys.never'),
    },
    {
      key: 'actions',
      header: t('common.actions'),
      render: (_, row) => (
        <div className="flex gap-2">
          {row.status === 'ACTIVE' && (
            <Button
              size="sm"
              variant="secondary"
              onClick={() => handleRevoke(row.id as string)}
            >
              {t('common.revoke')}
            </Button>
          )}
          <Button
            size="sm"
            variant="danger"
            onClick={() => confirmDelete(row.id as string)}
          >
            {t('common.delete')}
          </Button>
        </div>
      ),
    },
  ];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('apiKeys.title')}</h1>
          <p className="text-gray-600 mt-1">{t('apiKeys.subtitle')}</p>
        </div>
        <Button onClick={() => {
          setFormData(initialFormData);
          setIsCreateModalOpen(true);
        }}>
          {t('apiKeys.createApiKey')}
        </Button>
      </div>

      <Card>
        <DataTable
          columns={columns}
          data={(apiKeys || []) as (ApiKey & Record<string, unknown>)[]}
          emptyMessage={t('apiKeys.emptyMessage')}
        />
      </Card>

      {/* Create API Key Modal */}
      <Modal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        title={t('apiKeys.createTitle')}
      >
        <div className="space-y-4">
          <Input
            label={t('apiKeys.nameLabel')}
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            placeholder={t('apiKeys.namePlaceholder')}
          />
          <Input
            label={t('apiKeys.allowedDomains')}
            value={formData.allowedDomains}
            onChange={(e) => setFormData({ ...formData, allowedDomains: e.target.value })}
            placeholder={t('apiKeys.allowedDomainsPlaceholder')}
            hint={t('apiKeys.allowedDomainsHint')}
          />
          <Input
            label={t('apiKeys.allowedIps')}
            value={formData.allowedIps}
            onChange={(e) => setFormData({ ...formData, allowedIps: e.target.value })}
            placeholder={t('apiKeys.allowedIpsPlaceholder')}
            hint={t('apiKeys.allowedIpsHint')}
          />
          <div className="flex justify-end gap-3 pt-4">
            <Button variant="secondary" onClick={() => setIsCreateModalOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button
              onClick={handleCreate}
              disabled={!formData.name || createApiKey.isPending}
            >
              {createApiKey.isPending ? t('common.creating') : t('common.create')}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false);
          setKeyToDelete(null);
        }}
        title={t('apiKeys.deleteTitle')}
      >
        <div className="space-y-4">
          <p className="text-gray-600">
            {t('apiKeys.deleteConfirm')}
          </p>
          <div className="flex justify-end gap-3">
            <Button
              variant="secondary"
              onClick={() => {
                setDeleteModalOpen(false);
                setKeyToDelete(null);
              }}
            >
              {t('common.cancel')}
            </Button>
            <Button variant="danger" onClick={handleDelete} disabled={deleteApiKey.isPending}>
              {deleteApiKey.isPending ? t('common.deleting') : t('common.delete')}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Show New API Key Modal */}
      <Modal
        isOpen={isKeyModalOpen}
        onClose={() => setIsKeyModalOpen(false)}
        title={t('apiKeys.keyCreated')}
      >
        <div className="space-y-4">
          <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <p className="text-sm text-yellow-800 font-medium mb-2">
              {t('apiKeys.copyWarning')}
            </p>
            <p className="text-sm text-yellow-700">
              {t('apiKeys.copyWarningDesc')}
            </p>
          </div>
          <div className="flex gap-2">
            <code className="flex-1 p-3 bg-gray-100 rounded-lg text-sm break-all">
              {newApiKey}
            </code>
            <Button onClick={copyToClipboard}>
              {t('common.copy')}
            </Button>
          </div>
          <div className="flex justify-end pt-4">
            <Button onClick={() => setIsKeyModalOpen(false)}>
              {t('common.done')}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
